import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Modal } from '@/components/ui/Modal';
import { inputClass, labelClass, inlineErrorClass, inlineSuccessClass } from '@/components/ui/inputStyles';
import { cn } from '@/lib/cn';
import {
  defaultEntryStyle,
  ENTRY_STYLE_PRESETS_UI,
  ENTRY_STYLE_TEXT_KEYS,
  entryStyleForPreset,
  entryStylePresetLabel,
  entryStylePreviewCardStyle,
  entryStyleTextCss,
  mergeEntryStyleFromApi,
  type EntryStyleConfig,
  type EntryStylePreset,
  type EntryStyleTextKey,
} from '@/lib/oaMassPushEntryStyle';
import {
  ARTICLE_TITLE_VARS,
  defaultArticleTitle,
  mergeArticleTitleFromApi,
  resolveArticleTitlePreview,
  type ArticleTitleConfig,
} from '@/lib/oaMassPushArticleTitle';

type WechatAcc = {
  id: number;
  authorizer_appid: string;
  authorizer_nick_name: string;
  push_enabled: boolean;
};

type MpType = { key: string; label: string };

type EntrySiteOption = { id: number; site_name: string };

type LayoutTemplate = 'text' | 'text_mp' | 'text_image_mp';

type CoverConfig = {
  enabled: boolean;
  color_mode: 'fixed' | 'rotate' | 'random';
  preset: string;
  title_mode: 'auto' | 'custom';
  title_template: string;
  show_date: boolean;
  show_accent: boolean;
};

type OaMassPushConfig = {
  enabled: boolean;
  wechat_account_id: number;
  push_time_local: string;
  content_style: 'show_phone' | 'miniprogram_link';
  body_format: 'layout' | 'plain_separator' | 'rich_html';
  layout_template: LayoutTemplate;
  show_phone: boolean;
  show_info_no: boolean;
  mp_link_text: string;
  article_title: ArticleTitleConfig;
  article_author: string;
  cover: CoverConfig;
  entry_style: EntryStyleConfig;
  entry_separator: string;
  separator_image_width_px: number;
  order_mode: 'time_desc' | 'group_by_site';
  digest_miniprogram_id: number;
  catalog_miniprogram_id: number;
  days: number;
  max_items: number;
  type_keys: string[];
  catalog_site_ids: number[];
};

type MpRow = { id: number; name: string; enabled: boolean };

const DEFAULT_ENTRY_SEP = '——————————';

const COVER_PRESETS: { key: string; label: string; bg: string }[] = [
  { key: 'teal', label: '青绿', bg: '#0f766e' },
  { key: 'navy', label: '深蓝', bg: '#1e3a5f' },
  { key: 'slate', label: '岩灰', bg: '#334155' },
  { key: 'forest', label: '墨绿', bg: '#14532d' },
  { key: 'ink', label: '墨黑', bg: '#0f172a' },
  { key: 'amber', label: '琥珀', bg: '#92400e' },
];

const LAYOUT_OPTIONS: {
  key: LayoutTemplate;
  label: string;
  desc: string;
  icon: string;
}[] = [
  {
    key: 'text',
    label: '纯文字',
    desc: '简洁正文列表，适合无图汇总',
    icon: 'fa-align-left',
  },
  {
    key: 'text_mp',
    label: '文字 + 小程序',
    desc: '每条附可点的小程序详情链',
    icon: 'fa-link',
  },
  {
    key: 'text_image_mp',
    label: '文字 + 图 + 小程序',
    desc: '有配图时限高展示，并附详情链',
    icon: 'fa-image',
  },
];

const defaultCover = (): CoverConfig => ({
  enabled: true,
  color_mode: 'rotate',
  preset: 'teal',
  title_mode: 'auto',
  title_template: '{site_name}',
  show_date: true,
  show_accent: true,
});

const defaultConfig = (): OaMassPushConfig => ({
  enabled: false,
  wechat_account_id: 0,
  push_time_local: '09:00',
  content_style: 'show_phone',
  body_format: 'layout',
  layout_template: 'text',
  show_phone: true,
  show_info_no: true,
  mp_link_text: '查看详情',
  article_title: defaultArticleTitle(),
  article_author: '',
  cover: defaultCover(),
  entry_style: defaultEntryStyle(),
  entry_separator: DEFAULT_ENTRY_SEP,
  separator_image_width_px: 600,
  order_mode: 'time_desc',
  digest_miniprogram_id: 0,
  catalog_miniprogram_id: 0,
  days: 7,
  max_items: 30,
  type_keys: [],
  catalog_site_ids: [],
});

type OaMassPushRule = OaMassPushConfig & {
  id: string;
  name: string;
  last_run_ymd?: string;
};

const defaultRule = (): OaMassPushRule => ({
  id: '',
  name: '新推送规则',
  last_run_ymd: '',
  ...defaultConfig(),
});

function mergeCoverFromApi(raw: unknown): CoverConfig {
  const d = defaultCover();
  if (!raw || typeof raw !== 'object') return d;
  const c = raw as Record<string, unknown>;
  const mode = String(c.color_mode || '');
  const titleMode = String(c.title_mode || '');
  return {
    enabled: c.enabled === undefined ? d.enabled : Boolean(c.enabled),
    color_mode: mode === 'fixed' || mode === 'random' ? mode : 'rotate',
    preset: typeof c.preset === 'string' && c.preset.trim() ? c.preset.trim() : d.preset,
    title_mode: titleMode === 'custom' ? 'custom' : 'auto',
    title_template:
      typeof c.title_template === 'string' && c.title_template.trim()
        ? c.title_template.trim().slice(0, 40)
        : d.title_template,
    show_date: c.show_date === undefined ? d.show_date : Boolean(c.show_date),
    show_accent: c.show_accent === undefined ? d.show_accent : Boolean(c.show_accent),
  };
}

function inferLayout(raw: Record<string, unknown>): LayoutTemplate {
  const lt = String(raw.layout_template || '').trim();
  if (lt === 'text' || lt === 'text_mp' || lt === 'text_image_mp') return lt;
  const cs = String(raw.content_style || '');
  const sep = String(raw.entry_separator || '');
  if (cs === 'miniprogram_link' && /\{thumb_/i.test(sep)) return 'text_image_mp';
  if (cs === 'miniprogram_link') return 'text_mp';
  return 'text';
}

function mergeRuleFromApi(raw: unknown): OaMassPushRule {
  const c = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const base = defaultRule();
  const cfgIn = c as Partial<OaMassPushConfig> & Record<string, unknown>;
  const layout = inferLayout(c);
  const bfRaw = String(cfgIn.body_format || '');
  const body_format: OaMassPushConfig['body_format'] =
    bfRaw === 'rich_html' ? 'rich_html' : bfRaw === 'plain_separator' ? 'plain_separator' : 'layout';
  const showPhone =
    cfgIn.show_phone !== undefined
      ? Boolean(cfgIn.show_phone)
      : String(cfgIn.content_style || '') !== 'miniprogram_link';
  const mergedCfg: OaMassPushConfig = {
    ...defaultConfig(),
    ...cfgIn,
    digest_miniprogram_id: cfgIn.digest_miniprogram_id ?? 0,
    catalog_miniprogram_id: cfgIn.catalog_miniprogram_id ?? 0,
    days: typeof cfgIn.days === 'number' ? cfgIn.days : 7,
    max_items: typeof cfgIn.max_items === 'number' ? cfgIn.max_items : 30,
    type_keys: Array.isArray(cfgIn.type_keys)
      ? cfgIn.type_keys.map((x) => (typeof x === 'string' ? x : String(x)))
      : [],
    catalog_site_ids: Array.isArray(cfgIn.catalog_site_ids)
      ? cfgIn.catalog_site_ids
          .map((x) => (typeof x === 'number' && !Number.isNaN(x) ? Math.round(x) : parseInt(String(x), 10)))
          .filter((n) => n > 0)
      : [],
    body_format,
    layout_template: layout,
    show_phone: showPhone,
    show_info_no: cfgIn.show_info_no === undefined ? true : Boolean(cfgIn.show_info_no),
    mp_link_text:
      typeof cfgIn.mp_link_text === 'string' && cfgIn.mp_link_text.trim()
        ? cfgIn.mp_link_text.trim().slice(0, 40)
        : '查看详情',
    article_title: mergeArticleTitleFromApi(cfgIn.article_title),
    article_author:
      typeof cfgIn.article_author === 'string' ? cfgIn.article_author.trim().slice(0, 8) : '',
    cover: mergeCoverFromApi(cfgIn.cover),
    entry_style: mergeEntryStyleFromApi(cfgIn.entry_style),
    entry_separator:
      typeof cfgIn.entry_separator === 'string' && cfgIn.entry_separator.trim() !== ''
        ? cfgIn.entry_separator
        : DEFAULT_ENTRY_SEP,
    separator_image_width_px:
      typeof cfgIn.separator_image_width_px === 'number' && !Number.isNaN(cfgIn.separator_image_width_px)
        ? Math.min(900, Math.max(64, Math.round(cfgIn.separator_image_width_px)))
        : 600,
    content_style:
      layout === 'text_mp' || layout === 'text_image_mp'
        ? 'miniprogram_link'
        : showPhone
          ? 'show_phone'
          : 'miniprogram_link',
  };
  return {
    ...mergedCfg,
    id: typeof c.id === 'string' ? c.id : '',
    name: typeof c.name === 'string' && c.name.trim() !== '' ? String(c.name).trim() : base.name,
    last_run_ymd: typeof c.last_run_ymd === 'string' ? c.last_run_ymd : '',
  };
}

const UNCAT_KEY = '__uncat__';

function typeKeyForApi(uiKey: string): string {
  return uiKey === UNCAT_KEY ? '' : uiKey;
}

function layoutLabel(t: LayoutTemplate): string {
  return LAYOUT_OPTIONS.find((x) => x.key === t)?.label || t;
}

export function WechatOfficialPushPage() {
  const { currentSiteId, currentSiteName } = useSession();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [accounts, setAccounts] = useState<WechatAcc[]>([]);
  const [rules, setRules] = useState<OaMassPushRule[]>([]);
  const [lastRunYmd, setLastRunYmd] = useState('');
  const [cronHint, setCronHint] = useState('');
  const [miniprograms, setMiniprograms] = useState<MpRow[]>([]);
  const [catalogTypes, setCatalogTypes] = useState<MpType[]>([]);
  const [catalogEntrySites, setCatalogEntrySites] = useState<EntrySiteOption[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [editorTab, setEditorTab] = useState<'basic' | 'catalog' | 'body' | 'title' | 'style'>('basic');
  const [ruleDraft, setRuleDraft] = useState<OaMassPushRule>(defaultRule);
  const [helpOpen, setHelpOpen] = useState(false);
  const [previewBusyRuleId, setPreviewBusyRuleId] = useState<string | null>(null);
  const [runBusyRuleId, setRunBusyRuleId] = useState<string | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewHead, setPreviewHead] = useState('');
  const [previewClipboard, setPreviewClipboard] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewBodyFormat, setPreviewBodyFormat] = useState<'layout' | 'plain_separator' | 'rich_html'>('layout');
  const [previewActiveRuleId, setPreviewActiveRuleId] = useState('');
  const [copyTip, setCopyTip] = useState('');

  const needsMpLink = ruleDraft.layout_template === 'text_mp' || ruleDraft.layout_template === 'text_image_mp';

  const coverPreviewTitle = useMemo(() => {
    const site = (currentSiteName || '').trim() || `站点 #${currentSiteId}`;
    if (ruleDraft.cover.title_mode === 'custom') {
      return (ruleDraft.cover.title_template || '{site_name}')
        .replace(/\{site_name\}/g, site)
        .replace(/\{rule_name\}/g, ruleDraft.name || '推送规则')
        .replace(/\{count\}/g, '12')
        .replace(/\{date\}/g, new Date().toLocaleDateString('zh-CN'))
        .slice(0, 40);
    }
    return `${site} · 12条`.slice(0, 40);
  }, [currentSiteId, currentSiteName, ruleDraft.cover, ruleDraft.name]);

  const articleTitlePreview = useMemo(() => {
    return resolveArticleTitlePreview(ruleDraft.article_title, {
      site_name: (currentSiteName || '').trim() || `站点 #${currentSiteId}`,
      rule_name: ruleDraft.name || '推送规则',
      count: '12',
    });
  }, [currentSiteId, currentSiteName, ruleDraft.article_title, ruleDraft.name]);

  const loadCatalogTypes = useCallback(async (mpId: number) => {
    if (mpId < 1) {
      setCatalogTypes([]);
      setCatalogEntrySites([]);
      return;
    }
    setTypesLoading(true);
    try {
      const r = await apiFetch<{
        ok: boolean;
        miniprogram?: { types?: MpType[]; entry_site_options?: unknown[] };
      }>('get_miniprogram', {
        method: 'GET',
        query: { id: mpId },
      });
      if (r.ok && r.miniprogram?.types) {
        setCatalogTypes(r.miniprogram.types);
      } else {
        setCatalogTypes([]);
      }
      const rawOpts =
        r.ok && r.miniprogram && Array.isArray(r.miniprogram.entry_site_options)
          ? r.miniprogram.entry_site_options
          : [];
      const sites: EntrySiteOption[] = [];
      for (const o of rawOpts) {
        if (o && typeof o === 'object') {
          const rec = o as Record<string, unknown>;
          const id = typeof rec.id === 'number' ? rec.id : parseInt(String(rec.id ?? 0), 10);
          if (id > 0) {
            sites.push({
              id,
              site_name: typeof rec.site_name === 'string' ? rec.site_name : String(rec.site_name ?? ''),
            });
          }
        }
      }
      sites.sort((a, b) => a.id - b.id);
      setCatalogEntrySites(sites);
    } catch {
      setCatalogTypes([]);
      setCatalogEntrySites([]);
    } finally {
      setTypesLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    if (currentSiteId < 1) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr('');
    setMsg('');
    try {
      const [gc, mp] = await Promise.all([
        apiFetch<{
          ok: boolean;
          rules?: unknown[];
          wechat_accounts?: WechatAcc[];
          last_run_ymd?: string;
          cron_oa_mass_push_url_hint?: string;
        }>('get_oa_mass_push_config', {
          method: 'GET',
          query: { site_id: currentSiteId },
        }),
        apiFetch<{ ok: boolean; items: MpRow[] }>('list_miniprograms', {
          method: 'GET',
          query: { page: 1, per_page: 200 },
        }),
      ]);
      if (!gc.ok) {
        setErr('无法加载公众号推送配置');
        return;
      }
      const list = Array.isArray(gc.rules) ? gc.rules.map((x) => mergeRuleFromApi(x)) : [];
      setRules(list);
      setAccounts(gc.wechat_accounts ?? []);
      setLastRunYmd((gc.last_run_ymd ?? '').trim());
      setCronHint((gc.cron_oa_mass_push_url_hint ?? '').trim());
      if (mp.ok) {
        setMiniprograms(mp.items ?? []);
      }
      const firstMp = list[0]?.catalog_miniprogram_id ?? 0;
      await loadCatalogTypes(firstMp);
    } catch (e) {
      if (e instanceof Error && e.message === 'SESSION_EXPIRED') {
        return;
      }
      setErr(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [currentSiteId, loadCatalogTypes]);

  useEffect(() => {
    void load();
  }, [load]);

  function patchCover(partial: Partial<CoverConfig>) {
    setRuleDraft((c) => ({ ...c, cover: { ...c.cover, ...partial } }));
  }

  function patchArticleTitle(partial: Partial<ArticleTitleConfig>) {
    setRuleDraft((c) => ({ ...c, article_title: { ...c.article_title, ...partial } }));
  }

  function insertArticleTitleVar(token: string) {
    setRuleDraft((c) => {
      const cur = c.article_title.template || '';
      const next = (cur + token).slice(0, 64);
      return { ...c, article_title: { ...c.article_title, mode: 'custom', template: next } };
    });
  }

  function setLayout(tpl: LayoutTemplate) {
    setRuleDraft((c) => ({
      ...c,
      layout_template: tpl,
      body_format: 'layout',
      content_style: tpl === 'text' ? (c.show_phone ? 'show_phone' : 'miniprogram_link') : 'miniprogram_link',
    }));
  }

  function setEntryStylePreset(preset: EntryStylePreset) {
    setRuleDraft((c) => ({ ...c, entry_style: entryStyleForPreset(preset) }));
  }

  function patchEntryStyleText(key: EntryStyleTextKey, partial: Partial<EntryStyleConfig[EntryStyleTextKey]>) {
    setRuleDraft((c) => ({
      ...c,
      entry_style: {
        ...c.entry_style,
        [key]: { ...c.entry_style[key], ...partial },
      },
    }));
  }

  function patchEntryStyleCard(partial: Partial<EntryStyleConfig>) {
    setRuleDraft((c) => ({ ...c, entry_style: { ...c.entry_style, ...partial } }));
  }

  function toggleCatalogSiteId(siteId: number) {
    setRuleDraft((c) => {
      const set = new Set(c.catalog_site_ids);
      if (set.has(siteId)) set.delete(siteId);
      else set.add(siteId);
      return { ...c, catalog_site_ids: Array.from(set).sort((a, b) => a - b) };
    });
  }

  function toggleRuleTypeKey(uiKey: string) {
    const apiKey = typeKeyForApi(uiKey);
    setRuleDraft((c) => {
      const set = new Set(c.type_keys);
      if (set.has(apiKey)) set.delete(apiKey);
      else set.add(apiKey);
      return { ...c, type_keys: [...set] };
    });
  }

  function openCreateRule() {
    setErr('');
    setRuleDraft(defaultRule());
    setEditorTab('basic');
    setEditorModalOpen(true);
    void loadCatalogTypes(0);
  }

  function openEditRule(rule: OaMassPushRule) {
    setErr('');
    const next = { ...rule, body_format: 'layout' as const };
    setRuleDraft(next);
    setEditorTab('basic');
    setEditorModalOpen(true);
    void loadCatalogTypes(rule.catalog_miniprogram_id);
  }

  async function saveRuleFromEditor() {
    if (currentSiteId < 1) return;
    setSaving(true);
    setErr('');
    setMsg('');
    try {
      const payload: OaMassPushRule = {
        ...ruleDraft,
        body_format: 'layout',
        content_style:
          ruleDraft.layout_template === 'text'
            ? ruleDraft.show_phone
              ? 'show_phone'
              : 'miniprogram_link'
            : 'miniprogram_link',
      };
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string; rule?: OaMassPushRule }>(
        'save_oa_mass_push_rule',
        {
          method: 'POST',
          body: JSON.stringify({ site_id: currentSiteId, rule: payload }),
        },
      );
      if (!r.ok) {
        const msg = [r.err, r.detail].filter(Boolean).join('：');
        setErr(msg || '保存失败');
        return;
      }
      setMsg('已保存规则');
      setEditorModalOpen(false);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  }

  async function deleteRuleFromEditor() {
    if (currentSiteId < 1 || !ruleDraft.id) return;
    if (!window.confirm('确定删除此推送规则？不可恢复。')) return;
    setSaving(true);
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('delete_oa_mass_push_rule', {
        method: 'POST',
        body: JSON.stringify({ site_id: currentSiteId, rule_id: ruleDraft.id }),
      });
      if (!r.ok) {
        setErr(r.err || '删除失败');
        return;
      }
      setMsg('已删除规则');
      setEditorModalOpen(false);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : '删除失败');
    } finally {
      setSaving(false);
    }
  }

  async function runPreview(ruleId: string) {
    if (currentSiteId < 1 || !ruleId) return;
    setPreviewBusyRuleId(ruleId);
    setPreviewModalOpen(false);
    setCopyTip('');
    setErr('');
    setPreviewActiveRuleId(ruleId);
    try {
      const r = await apiFetch<{
        ok: boolean;
        err?: string;
        title?: string;
        digest?: string;
        html?: string;
        clipboard_text?: string;
        body_format?: string;
        entry_count?: number;
        skipped?: boolean;
        detail?: string;
      }>('preview_oa_mass_push', {
        method: 'GET',
        query: { site_id: currentSiteId, rule_id: ruleId },
      });
      if (!r.ok) {
        setErr(r.err || '预览失败');
        return;
      }
      if (r.skipped) {
        setErr('');
        const d = r.detail || '';
        setMsg(
          d === 'no_push_rules'
            ? '暂无推送规则，请先点击「创建推送规则」并保存。'
            : `预览跳过：${d || '无条目'}`,
        );
        return;
      }
      const head = `草稿标题：${r.title || '—'}\n摘要：${r.digest || '—'}\n条数：${r.entry_count ?? 0}`;
      setPreviewHead(head);
      const html = (r.html ?? '').trim();
      setPreviewHtml(html);
      setPreviewClipboard((r.clipboard_text ?? '').trim() !== '' ? r.clipboard_text! : html);
      const bf = r.body_format;
      setPreviewBodyFormat(bf === 'rich_html' || bf === 'plain_separator' ? bf : 'layout');
      setPreviewModalOpen(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : '预览失败');
    } finally {
      setPreviewBusyRuleId(null);
    }
  }

  async function copyPreviewToClipboard() {
    const t = previewClipboard || previewHtml;
    if (!t) {
      setCopyTip('无可复制内容');
      return;
    }
    let text = t;
    let wechatImgRewritten = 0;
    if (
      currentSiteId >= 1 &&
      /<\s*img\b/i.test(t) &&
      !/mmbiz\.qpic\.cn/i.test(t)
    ) {
      setCopyTip('正在上传图片到公众号素材…');
      try {
        const r = await apiFetch<{
          ok: boolean;
          err?: string;
          clipboard_text?: string;
          images_rewritten?: number;
        }>('oa_mass_push_clipboard_for_wechat', {
          method: 'POST',
          body: JSON.stringify({ site_id: currentSiteId, rule_id: previewActiveRuleId }),
        });
        if (!r.ok) {
          setCopyTip(r.err || '处理失败');
          window.setTimeout(() => setCopyTip(''), 4200);
          return;
        }
        wechatImgRewritten = typeof r.images_rewritten === 'number' ? r.images_rewritten : 0;
        text = (r.clipboard_text ?? t).trim() || t;
      } catch (e) {
        setCopyTip(e instanceof Error ? e.message : '请求失败');
        window.setTimeout(() => setCopyTip(''), 4200);
        return;
      }
    }
    const looksLikeHtml = /<\s*(?:p|img|div|section|br|h[1-6]|a)\b/i.test(text);
    if (looksLikeHtml && typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      try {
        const docHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body>${text}</body></html>`;
        const htmlBlob = new Blob([docHtml], { type: 'text/html' });
        let plain = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        try {
          const doc = new DOMParser().parseFromString(`<div>${text}</div>`, 'text/html');
          const tx = doc.body.textContent?.replace(/\n{3,}/g, '\n\n').trim();
          if (tx) plain = tx;
        } catch {
          /* keep */
        }
        const plainBlob = new Blob([plain], { type: 'text/plain' });
        await navigator.clipboard.write([new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': plainBlob })]);
        setCopyTip(
          wechatImgRewritten > 0
            ? `已复制（已将 ${wechatImgRewritten} 张图上传为公众号素材）`
            : '已复制（含 HTML，可粘贴到公众号正文）',
        );
        window.setTimeout(() => setCopyTip(''), 3200);
        return;
      } catch {
        /* fall through */
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopyTip('已复制到剪贴板');
    } catch {
      setCopyTip('复制失败，请手动全选下方文本复制');
    }
    window.setTimeout(() => setCopyTip(''), 2800);
  }

  async function runPushNow(ruleId: string) {
    if (currentSiteId < 1 || !ruleId) return;
    if (
      !window.confirm(
        '将向所选公众号写入一条「汇总」图文草稿（优先使用模板封面；失败时回退信息配图）。确定继续？',
      )
    ) {
      return;
    }
    setRunBusyRuleId(ruleId);
    setErr('');
    setMsg('');
    try {
      const r = await apiFetch<{
        ok: boolean;
        err?: string;
        detail?: string;
        media_id?: string;
        title?: string;
        entry_count?: number;
        skipped?: boolean;
      }>('oa_mass_draft_now', {
        method: 'POST',
        // 勿传 ignore_enabled:true：线上 WAF 会误拦该字段并返回 SPA HTML 404
        body: JSON.stringify({ site_id: currentSiteId, rule_id: ruleId }),
      });
      if (r.skipped) {
        setErr('未执行：' + (r.detail || '未知原因'));
        return;
      }
      if (!r.ok) {
        setErr(r.err || r.detail || '执行失败');
        return;
      }
      setMsg(`已写入公众号草稿箱。media_id=${r.media_id || '—'}，标题：${r.title || '—'}，共 ${r.entry_count ?? 0} 条信息。`);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : '执行失败');
    } finally {
      setRunBusyRuleId(null);
    }
  }

  function accountNick(accId: number): string {
    const a = accounts.find((x) => x.id === accId);
    if (!a) return accId > 0 ? `账号 #${accId}` : '（未选择公众号）';
    return (a.authorizer_nick_name || a.authorizer_appid || `账号 #${accId}`) + (a.push_enabled ? '' : ' · 推送已关');
  }

  function mpTitle(mpId: number): string {
    const m = miniprograms.find((x) => x.id === mpId);
    if (!m) return mpId > 0 ? `小程序 #${mpId}` : '（未选择小程序）';
    return `#${m.id} ${m.name}${!m.enabled ? '（已停用）' : ''}`;
  }

  if (currentSiteId < 1) {
    return (
      <p className="text-sm text-amber-800">请先在上方「当前工作站点」中选择站点后再配置公众号推送。</p>
    );
  }

  const presetBg = COVER_PRESETS.find((p) => p.key === ruleDraft.cover.preset)?.bg || '#0f766e';

  return (
    <div className="space-y-6">
      <PageHeader
        title="公众号推送"
        description="多规则定时汇总：选版式、配封面，写入公众号草稿箱。"
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setHelpOpen(true)}>
              说明
            </Button>
            <Button type="button" variant="secondary" onClick={() => void load()} disabled={loading}>
              重新加载
            </Button>
            <Link to="/interfaces">
              <Button type="button" variant="secondary">
                返回接入与推送
              </Button>
            </Link>
            <Button type="button" variant="primary" onClick={() => openCreateRule()} disabled={loading}>
              创建推送规则
            </Button>
          </div>
        }
      />

      {loading ? (
        <Spinner label="加载配置" />
      ) : (
        <>
          {err ? <p className={inlineErrorClass}>{err}</p> : null}
          {msg ? <p className={inlineSuccessClass}>{msg}</p> : null}

          {rules.length === 0 ? (
            <Card title="推送规则">
              <p className="text-sm text-slate-600">
                暂无规则。点击右上角「创建推送规则」添加；保存后可「预览汇总」「生成草稿」。
              </p>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {rules.map((rule) => (
                <div
                  key={rule.id || rule.name}
                  className="flex flex-col rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-base font-semibold text-slate-900">
                        {rule.name || '未命名规则'}
                      </h3>
                      <p className="mt-1 text-xs text-slate-500">
                        {rule.enabled ? <span className="text-emerald-700">已启用</span> : <span>未启用</span>}
                        {' · '}
                        {rule.push_time_local}
                        {' · '}
                        {accountNick(rule.wechat_account_id)}
                      </p>
                      <p className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600">
                        <span>
                          版式：
                          <strong className="font-medium text-slate-800">
                            {layoutLabel(rule.layout_template)}
                          </strong>
                        </span>
                        <span>
                          样式：
                          <strong className="font-medium text-slate-800">
                            {entryStylePresetLabel(rule.entry_style?.preset || 'card_teal')}
                          </strong>
                        </span>
                        <span>
                          电话：
                          <strong className="font-medium text-slate-800">
                            {rule.show_phone ? '显示' : '脱敏'}
                          </strong>
                        </span>
                        <span>
                          编号：
                          <strong className="font-medium text-slate-800">
                            {rule.show_info_no !== false ? '显示' : '隐藏'}
                          </strong>
                        </span>
                        <span>
                          封面：
                          <strong className="font-medium text-slate-800">
                            {rule.cover?.enabled !== false ? '模板图' : '信息配图'}
                          </strong>
                        </span>
                      </p>
                      <p className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600">
                        <span>
                          取数：
                          <strong className="font-medium text-slate-800">
                            {mpTitle(rule.catalog_miniprogram_id)}
                          </strong>
                        </span>
                        <span>
                          <strong className="font-medium text-slate-800">{rule.days} 天</strong> / 最多{' '}
                          <strong className="font-medium text-slate-800">{rule.max_items}</strong>
                        </span>
                      </p>
                      {rule.last_run_ymd ? (
                        <p className="mt-1 text-xs text-slate-500">
                          上次成功：<span className="font-mono">{rule.last_run_ymd}</span>
                        </p>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="shrink-0 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                      onClick={() => openEditRule(rule)}
                    >
                      编辑
                    </button>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={!rule.id || previewBusyRuleId === rule.id}
                      onClick={() => void runPreview(rule.id)}
                    >
                      {previewBusyRuleId === rule.id ? '预览中…' : '预览汇总'}
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      disabled={!rule.id || runBusyRuleId === rule.id}
                      onClick={() => void runPushNow(rule.id)}
                    >
                      {runBusyRuleId === rule.id ? '写入中…' : '生成草稿'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Modal open={helpOpen} title="公众号推送说明" wide onClose={() => setHelpOpen(false)}>
            <div className="space-y-3 text-sm leading-relaxed text-slate-600">
              <p>
                保存规则后，可由<strong>计划任务</strong>在配置时刻窗口内自动生成<strong>一条汇总图文草稿</strong>
                写入所选公众号草稿箱。同一自然日成功执行一次后会跳过，避免重复草稿。
              </p>
              <p>
                推送目标须为当前站点已绑定账号。正文使用三套常用版式；封面默认由服务端生成
                <strong> 2.35:1 模板图</strong>。可配置草稿标题、作者、信息编号与条目样式。
              </p>
              <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2.5 text-xs leading-relaxed text-slate-700">
                <p className="mb-1.5 font-semibold text-slate-800">宝塔计划任务配置步骤</p>
                <ol className="list-decimal space-y-1 pl-4">
                  <li>登录宝塔 → 左侧「计划任务」→「添加任务」</li>
                  <li>任务类型选「访问 URL」；任务名称如「贴图虾公众号汇总推送」</li>
                  <li>
                    执行周期建议选「N 分钟」→ <strong>每 10 分钟</strong>
                    （系统会在规则设定时刻 ±12 分钟窗口内真正写入草稿）
                  </li>
                  <li>
                    URL 填下方示例（把 cron_key 换成服务器{' '}
                    <code className="rounded bg-white px-1">dist/api/config.php</code> 里的{' '}
                    <code className="rounded bg-white px-1">cron_key</code>）
                  </li>
                  <li>保存后可点「执行」试跑；返回 JSON 且含 items 即表示任务可达</li>
                </ol>
                {cronHint ? (
                  <p className="mt-2 break-all font-mono text-[11px] text-slate-800">{cronHint}</p>
                ) : (
                  <p className="mt-2 font-mono text-[11px] text-slate-800">
                    https://wes.ddxd.fun/api/api.php?action=cron_oa_mass_push&amp;key=你的cron_key&amp;dry=0
                  </p>
                )}
                <p className="mt-2 text-slate-500">
                  注意：规则须「启用」并选好公众号；时刻与计划任务触发对齐（例如规则 06:00，则 05:48～06:12
                  内被扫到才会写草稿）。dry=1 仅演练不写草稿。
                </p>
              </div>
              {lastRunYmd ? (
                <p className="text-xs text-slate-600">
                  站点上次成功摘要草稿日期：<span className="font-mono">{lastRunYmd}</span>
                </p>
              ) : null}
            </div>
          </Modal>

          <Modal
            open={editorModalOpen}
            title={ruleDraft.id ? '编辑推送规则' : '创建推送规则'}
            wide="xl"
            onClose={() => {
              if (!saving) setEditorModalOpen(false);
            }}
            footer={
              <div className="flex w-full flex-wrap items-center gap-2">
                {ruleDraft.id ? (
                  <Button
                    type="button"
                    variant="secondary"
                    className="border-red-200 text-red-700 hover:bg-red-50"
                    onClick={() => void deleteRuleFromEditor()}
                    disabled={saving}
                  >
                    删除此规则
                  </Button>
                ) : null}
                <span className="flex-1" />
                <Button type="button" variant="secondary" onClick={() => setEditorModalOpen(false)} disabled={saving}>
                  取消
                </Button>
                <Button type="button" variant="primary" disabled={saving} onClick={() => void saveRuleFromEditor()}>
                  {saving ? '保存中…' : '保存规则'}
                </Button>
              </div>
            }
          >
            <div className="flex max-h-[min(76vh,720px)] flex-col gap-3">
              <div className="flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1">
                {(
                  [
                    { key: 'basic' as const, label: '1 基础' },
                    { key: 'catalog' as const, label: '2 取数' },
                    { key: 'body' as const, label: '3 正文' },
                    { key: 'title' as const, label: '4 标题封面' },
                    { key: 'style' as const, label: '5 样式' },
                  ] as const
                ).map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setEditorTab(tab.key)}
                    className={cn(
                      'rounded-lg px-3 py-1.5 text-xs font-medium transition sm:text-sm',
                      editorTab === tab.key
                        ? 'bg-white text-teal-800 shadow-sm ring-1 ring-teal-200'
                        : 'text-slate-600 hover:bg-white/70',
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
                {editorTab === 'basic' ? (
                  <>
                    <label className={labelClass}>
                      <span>规则名称</span>
                      <input
                        type="text"
                        className={inputClass}
                        maxLength={80}
                        value={ruleDraft.name}
                        onChange={(e) => setRuleDraft((c) => ({ ...c, name: e.target.value.slice(0, 80) }))}
                        placeholder="例如：每日二手汇总"
                      />
                    </label>
                    <div className="grid gap-4 md:grid-cols-2">
                      <label className={`${labelClass} flex items-center gap-2 md:col-span-2`}>
                        <input
                          type="checkbox"
                          checked={ruleDraft.enabled}
                          onChange={(e) => setRuleDraft((c) => ({ ...c, enabled: e.target.checked }))}
                        />
                        <span>启用本规则（计划任务识别）</span>
                      </label>
                      <label className={labelClass}>
                        <span>推送目标公众号</span>
                        <select
                          className={inputClass}
                          value={ruleDraft.wechat_account_id || ''}
                          onChange={(e) =>
                            setRuleDraft((c) => ({ ...c, wechat_account_id: Number(e.target.value) || 0 }))
                          }
                        >
                          <option value="">（未选择）</option>
                          {accounts.map((a) => (
                            <option key={a.id} value={a.id}>
                              {(a.authorizer_nick_name || a.authorizer_appid || '未命名') +
                                (a.push_enabled ? '' : ' · 推送已关')}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className={labelClass}>
                        <span>每日推送时刻（服务器本地时区）</span>
                        <input
                          type="time"
                          className={inputClass}
                          value={ruleDraft.push_time_local.length === 5 ? ruleDraft.push_time_local : '09:00'}
                          onChange={(e) =>
                            setRuleDraft((c) => ({ ...c, push_time_local: e.target.value.slice(0, 5) }))
                          }
                        />
                      </label>
                      <label className={`${labelClass} md:col-span-2`}>
                        <span>排序方式</span>
                        <select
                          className={inputClass}
                          value={ruleDraft.order_mode}
                          onChange={(e) => {
                            const v = e.target.value;
                            setRuleDraft((c) => ({
                              ...c,
                              order_mode: v === 'group_by_site' ? 'group_by_site' : 'time_desc',
                            }));
                          }}
                        >
                          <option value="time_desc">与小程序目录相同排序</option>
                          <option value="group_by_site">按站点分块，块内按创建时间倒序</option>
                        </select>
                      </label>
                    </div>
                  </>
                ) : null}

                {editorTab === 'catalog' ? (
                  <>
                    <p className="text-sm text-slate-600">
                      数据来自该小程序在「小程序管理」中已勾选站点下的<strong>展示中</strong>信息。
                    </p>
                    <div className="grid gap-4 md:grid-cols-2">
                      <label className={`${labelClass} md:col-span-2`}>
                        <span>展示用微信小程序</span>
                        <select
                          className={inputClass}
                          value={ruleDraft.catalog_miniprogram_id || ''}
                          onChange={(e) => {
                            const id = Number(e.target.value) || 0;
                            setRuleDraft((c) => ({
                              ...c,
                              catalog_miniprogram_id: id,
                              type_keys: [],
                              catalog_site_ids: [],
                            }));
                            void loadCatalogTypes(id);
                          }}
                        >
                          <option value="">（请选择）</option>
                          {miniprograms.map((m) => (
                            <option key={m.id} value={m.id}>
                              #{m.id} {m.name}
                              {!m.enabled ? '（已停用）' : ''}
                            </option>
                          ))}
                        </select>
                      </label>
                      {ruleDraft.catalog_miniprogram_id >= 1 ? (
                        <div className={`${labelClass} md:col-span-2`}>
                          <span>取数站点（多选）</span>
                          <p className="mb-2 text-xs leading-relaxed text-slate-500">
                            不勾选表示<strong>该小程序全部展示站点</strong>；勾选后仅从所选站点拉取。
                          </p>
                          {typesLoading ? (
                            <p className="text-sm text-slate-500">加载站点列表中…</p>
                          ) : catalogEntrySites.length === 0 ? (
                            <p className="text-sm text-slate-600">该小程序暂无可用展示站点。</p>
                          ) : (
                            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/80 p-3">
                              <button
                                type="button"
                                className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                                onClick={() =>
                                  setRuleDraft((c) => ({
                                    ...c,
                                    catalog_site_ids: catalogEntrySites.map((s) => s.id),
                                  }))
                                }
                              >
                                全选
                              </button>
                              <span className="text-slate-300">|</span>
                              <button
                                type="button"
                                className="text-xs font-medium text-slate-600 hover:text-slate-800"
                                onClick={() => setRuleDraft((c) => ({ ...c, catalog_site_ids: [] }))}
                              >
                                清除（全部站点）
                              </button>
                              <div className="mt-2 flex w-full flex-wrap gap-x-4 gap-y-2">
                                {catalogEntrySites.map((s) => (
                                  <label key={s.id} className="flex cursor-pointer items-center gap-2 text-sm">
                                    <input
                                      type="checkbox"
                                      checked={ruleDraft.catalog_site_ids.includes(s.id)}
                                      onChange={() => toggleCatalogSiteId(s.id)}
                                    />
                                    <span>
                                      <span className="font-mono text-xs">#{s.id}</span>
                                      {s.site_name ? <span className="text-slate-600"> · {s.site_name}</span> : null}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null}
                      <label className={labelClass}>
                        <span>创建时间在几天内</span>
                        <input
                          type="number"
                          min={1}
                          max={365}
                          className={inputClass}
                          value={ruleDraft.days}
                          onChange={(e) =>
                            setRuleDraft((c) => ({
                              ...c,
                              days: Math.min(365, Math.max(1, parseInt(e.target.value, 10) || 7)),
                            }))
                          }
                        />
                      </label>
                      <label className={labelClass}>
                        <span>最多取几条</span>
                        <input
                          type="number"
                          min={1}
                          max={100}
                          className={inputClass}
                          value={ruleDraft.max_items}
                          onChange={(e) =>
                            setRuleDraft((c) => ({
                              ...c,
                              max_items: Math.min(100, Math.max(1, parseInt(e.target.value, 10) || 30)),
                            }))
                          }
                        />
                      </label>
                    </div>
                    <div className="border-t border-slate-200 pt-4">
                      <p className="mb-2 text-sm font-medium text-slate-800">信息类型（可多选）</p>
                      <p className="mb-2 text-xs text-slate-500">不勾选表示不按类型过滤。</p>
                      {ruleDraft.catalog_miniprogram_id < 1 ? (
                        <p className="text-sm text-slate-500">请先选择微信小程序。</p>
                      ) : typesLoading ? (
                        <p className="text-sm text-slate-500">加载类型中…</p>
                      ) : catalogTypes.length === 0 ? (
                        <p className="text-sm text-slate-600">该小程序尚未配置信息类型。</p>
                      ) : null}
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2 rounded-lg border border-slate-200 bg-slate-50/80 p-3">
                        <label className="flex cursor-pointer items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={ruleDraft.type_keys.includes('')}
                            onChange={() => toggleRuleTypeKey(UNCAT_KEY)}
                          />
                          <span>未分类（无 type_key）</span>
                        </label>
                        {catalogTypes.map((t) => (
                          <label key={t.key} className="flex cursor-pointer items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={ruleDraft.type_keys.includes(t.key)}
                              onChange={() => toggleRuleTypeKey(t.key)}
                            />
                            <span>
                              <span className="font-mono text-xs">{t.key}</span>
                              {t.label ? <span className="text-slate-600"> · {t.label}</span> : null}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                ) : null}

                {editorTab === 'body' ? (
                  <>
                    <div className="grid gap-2 sm:grid-cols-3">
                      {LAYOUT_OPTIONS.map((opt) => {
                        const active = ruleDraft.layout_template === opt.key;
                        return (
                          <button
                            key={opt.key}
                            type="button"
                            onClick={() => setLayout(opt.key)}
                            className={cn(
                              'rounded-xl border px-3 py-3 text-left transition',
                              active
                                ? 'border-teal-400 bg-teal-50/80 ring-1 ring-teal-200'
                                : 'border-slate-200 bg-white hover:border-slate-300',
                            )}
                          >
                            <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                              <i className={cn('fa-solid text-teal-700/80', opt.icon)} aria-hidden />
                              {opt.label}
                            </span>
                            <span className="mt-1 block text-[11px] leading-snug text-slate-500">{opt.desc}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <label className={`${labelClass} flex items-start gap-2 md:col-span-2`}>
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={ruleDraft.show_phone}
                          onChange={(e) =>
                            setRuleDraft((c) => ({
                              ...c,
                              show_phone: e.target.checked,
                              content_style: e.target.checked ? 'show_phone' : 'miniprogram_link',
                            }))
                          }
                        />
                        <span>
                          <span className="font-medium text-slate-800">显示电话号码</span>
                          <span className="mt-0.5 block text-xs text-slate-500">
                            关闭后不展示电话行；正文/标题中的手机号中间 4 位替换为 XXXX。
                          </span>
                        </span>
                      </label>
                      <label className={`${labelClass} flex items-start gap-2 md:col-span-2`}>
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={ruleDraft.show_info_no}
                          onChange={(e) => setRuleDraft((c) => ({ ...c, show_info_no: e.target.checked }))}
                        />
                        <span>
                          <span className="font-medium text-slate-800">显示信息编号</span>
                          <span className="mt-0.5 block text-xs text-slate-500">
                            与小程序详情页一致的流水号（serial_number）；无流水号时回退为条目 ID。
                          </span>
                        </span>
                      </label>
                      {needsMpLink ? (
                        <label className={`${labelClass} md:col-span-2`}>
                          <span>小程序链接文案（每条信息详情页）</span>
                          <input
                            type="text"
                            className={inputClass}
                            maxLength={40}
                            value={ruleDraft.mp_link_text}
                            onChange={(e) =>
                              setRuleDraft((c) => ({ ...c, mp_link_text: e.target.value.slice(0, 40) }))
                            }
                            placeholder="查看详情"
                          />
                        </label>
                      ) : null}
                      {ruleDraft.layout_template === 'text_image_mp' ? (
                        <p className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2 text-xs leading-relaxed text-slate-600">
                          配图限高约 180px，有图才显示；无图时仅保留文字与小程序链。
                        </p>
                      ) : null}
                    </div>
                  </>
                ) : null}

                {editorTab === 'title' ? (
                  <>
                    <Card title="草稿箱图文标题">
                      <p className="mb-3 text-xs leading-relaxed text-slate-500">
                        写入公众号草稿箱的那一行标题（不是封面图文字）。微信限制约 64 字。
                      </p>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {(
                          [
                            { key: 'auto' as const, title: '自动', desc: '站点名 · 日期 · 条数' },
                            { key: 'custom' as const, title: '自定义模板', desc: '可插入下方变量' },
                          ] as const
                        ).map((opt) => {
                          const active = ruleDraft.article_title.mode === opt.key;
                          return (
                            <button
                              key={opt.key}
                              type="button"
                              onClick={() => patchArticleTitle({ mode: opt.key })}
                              className={cn(
                                'rounded-xl border px-3 py-2.5 text-left transition',
                                active
                                  ? 'border-teal-300 bg-white ring-1 ring-teal-200'
                                  : 'border-slate-200 bg-white/70 hover:border-slate-300',
                              )}
                            >
                              <span className="block text-sm font-semibold text-slate-900">{opt.title}</span>
                              <span className="mt-0.5 block text-[11px] text-slate-500">{opt.desc}</span>
                            </button>
                          );
                        })}
                      </div>
                      {ruleDraft.article_title.mode === 'custom' ? (
                        <div className="mt-3 space-y-2">
                          <input
                            className={inputClass}
                            maxLength={64}
                            value={ruleDraft.article_title.template}
                            onChange={(e) => patchArticleTitle({ template: e.target.value.slice(0, 64) })}
                            placeholder="{site_name} · {date}汇总"
                          />
                          <div className="flex flex-wrap gap-1.5">
                            {ARTICLE_TITLE_VARS.map((v) => (
                              <button
                                key={v.key}
                                type="button"
                                title={`${v.label} 示例 ${v.sample}`}
                                onClick={() => insertArticleTitleVar(v.key)}
                                className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-700 hover:border-teal-300 hover:bg-teal-50"
                              >
                                {v.key}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      <p className="mt-3 rounded-lg border border-teal-100 bg-teal-50/60 px-3 py-2 text-sm text-teal-900">
                        预览：<strong>{articleTitlePreview || '—'}</strong>
                      </p>
                      <label className={`${labelClass} mt-4`}>
                        <span>作者名字（草稿箱「请输入作者」）</span>
                        <input
                          type="text"
                          className={inputClass}
                          maxLength={8}
                          value={ruleDraft.article_author}
                          onChange={(e) =>
                            setRuleDraft((c) => ({ ...c, article_author: e.target.value.slice(0, 8) }))
                          }
                          placeholder="例如：南京二手"
                        />
                        <span className="mt-1 block text-xs text-slate-500">
                          写入微信图文的作者字段，最多 8 个字；留空则草稿箱仍显示「请输入作者」。
                        </span>
                      </label>
                    </Card>

                    <Card title="草稿封面（2.35:1）">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <p className="text-xs leading-relaxed text-slate-500">
                          服务端生成纯色底 + 标题/日期，上传为公众号图文封面。
                        </p>
                        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={ruleDraft.cover.enabled}
                            onChange={(e) => patchCover({ enabled: e.target.checked })}
                          />
                          生成模板封面
                        </label>
                      </div>
                      {ruleDraft.cover.enabled ? (
                        <div className="mt-3 space-y-3">
                          <div
                            className="relative overflow-hidden rounded-xl"
                            style={{ aspectRatio: '2.35 / 1', background: presetBg }}
                          >
                            <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
                              <p className="text-base font-semibold tracking-wide text-white sm:text-lg">
                                {coverPreviewTitle}
                              </p>
                              {ruleDraft.cover.show_accent ? (
                                <span className="mt-2 h-1 w-16 rounded-full bg-amber-400/90" />
                              ) : null}
                              {ruleDraft.cover.show_date ? (
                                <p className="mt-2 text-xs text-white/75">
                                  {new Date().toLocaleDateString('zh-CN', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  })}
                                </p>
                              ) : null}
                            </div>
                          </div>
                          <div>
                            <p className="mb-1.5 text-xs font-medium text-slate-600">配色</p>
                            <div className="flex flex-wrap gap-2">
                              {COVER_PRESETS.map((p) => (
                                <button
                                  key={p.key}
                                  type="button"
                                  title={p.label}
                                  onClick={() => patchCover({ preset: p.key, color_mode: ruleDraft.cover.color_mode })}
                                  className={cn(
                                    'h-8 w-8 rounded-full border-2 transition',
                                    ruleDraft.cover.preset === p.key
                                      ? 'border-white ring-2 ring-teal-500'
                                      : 'border-white/80 ring-1 ring-slate-200',
                                  )}
                                  style={{ background: p.bg }}
                                />
                              ))}
                            </div>
                          </div>
                          <label className={labelClass}>
                            <span>配色方式</span>
                            <select
                              className={inputClass}
                              value={ruleDraft.cover.color_mode}
                              onChange={(e) => {
                                const v = e.target.value;
                                patchCover({
                                  color_mode: v === 'fixed' || v === 'random' ? v : 'rotate',
                                });
                              }}
                            >
                              <option value="fixed">固定当前配色</option>
                              <option value="rotate">按序轮换</option>
                              <option value="random">随机（避免连续同色）</option>
                            </select>
                          </label>
                          <div>
                            <p className="mb-1.5 text-xs font-medium text-slate-600">封面文字</p>
                            <div className="grid gap-2 sm:grid-cols-2">
                              {(
                                [
                                  { key: 'auto' as const, title: '自动', desc: '站点名 + 条数' },
                                  {
                                    key: 'custom' as const,
                                    title: '自定义',
                                    desc: '{site_name}{rule_name}{count}{date}',
                                  },
                                ] as const
                              ).map((opt) => {
                                const active = ruleDraft.cover.title_mode === opt.key;
                                return (
                                  <button
                                    key={opt.key}
                                    type="button"
                                    onClick={() => patchCover({ title_mode: opt.key })}
                                    className={cn(
                                      'rounded-xl border px-3 py-2.5 text-left transition',
                                      active
                                        ? 'border-teal-300 bg-white ring-1 ring-teal-200'
                                        : 'border-slate-200 bg-white/70 hover:border-slate-300',
                                    )}
                                  >
                                    <span className="block text-sm font-semibold text-slate-900">{opt.title}</span>
                                    <span className="mt-0.5 block text-[11px] text-slate-500">{opt.desc}</span>
                                  </button>
                                );
                              })}
                            </div>
                            {ruleDraft.cover.title_mode === 'custom' ? (
                              <input
                                className={`${inputClass} mt-2`}
                                maxLength={40}
                                value={ruleDraft.cover.title_template}
                                onChange={(e) => patchCover({ title_template: e.target.value.slice(0, 40) })}
                                placeholder="{site_name}"
                              />
                            ) : null}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-slate-700">
                            <label className="inline-flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={ruleDraft.cover.show_date}
                                onChange={(e) => patchCover({ show_date: e.target.checked })}
                              />
                              显示日期
                            </label>
                            <label className="inline-flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={ruleDraft.cover.show_accent}
                                onChange={(e) => patchCover({ show_accent: e.target.checked })}
                              />
                              装饰短线
                            </label>
                          </div>
                        </div>
                      ) : (
                        <p className="mt-2 text-xs text-amber-800">
                          已关闭模板封面：写入草稿时需至少一条信息带有已落盘本地图，否则会失败。
                        </p>
                      )}
                    </Card>
                  </>
                ) : null}

                {editorTab === 'style' ? (
                  <>
                    <p className="text-xs leading-relaxed text-slate-500">
                      草稿正文中每条信息的卡片外观与文字样式；含信息编号、小程序链接均可单独设置。
                    </p>
                    <p className="mb-1.5 text-xs font-medium text-slate-600">样式预设（5 款）</p>
                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {ENTRY_STYLE_PRESETS_UI.map((opt) => {
                        const active = ruleDraft.entry_style.preset === opt.key;
                        return (
                          <button
                            key={opt.key}
                            type="button"
                            onClick={() => setEntryStylePreset(opt.key)}
                            className={cn(
                              'rounded-xl border px-3 py-2.5 text-left transition',
                              active
                                ? 'border-teal-400 bg-teal-50/80 ring-1 ring-teal-200'
                                : 'border-slate-200 bg-white hover:border-slate-300',
                            )}
                          >
                            <div
                              className="mb-2 rounded-lg border p-2"
                              style={{
                                background: opt.bg,
                                borderColor: opt.border,
                                borderLeftWidth: 4,
                                borderLeftColor: opt.accent,
                              }}
                            >
                              <span className="block text-xs font-semibold" style={{ color: opt.accent }}>
                                示例标题
                              </span>
                              <span className="mt-0.5 block text-[10px] text-slate-400">来源站点</span>
                            </div>
                            <span className="block text-sm font-semibold text-slate-900">{opt.label}</span>
                            <span className="mt-0.5 block text-[11px] text-slate-500">{opt.desc}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-2 grid gap-3 md:grid-cols-3">
                      <label className={labelClass}>
                        <span>卡片描边色</span>
                        <input
                          type="color"
                          className="h-10 w-full cursor-pointer rounded border border-slate-200 bg-white p-1"
                          value={ruleDraft.entry_style.card_border_color}
                          onChange={(e) => patchEntryStyleCard({ card_border_color: e.target.value })}
                        />
                      </label>
                      <label className={labelClass}>
                        <span>卡片背景色</span>
                        <input
                          type="color"
                          className="h-10 w-full cursor-pointer rounded border border-slate-200 bg-white p-1"
                          value={ruleDraft.entry_style.card_bg_color}
                          onChange={(e) => patchEntryStyleCard({ card_bg_color: e.target.value })}
                        />
                      </label>
                      <label className={labelClass}>
                        <span>强调色（色条/顶线）</span>
                        <input
                          type="color"
                          className="h-10 w-full cursor-pointer rounded border border-slate-200 bg-white p-1"
                          value={ruleDraft.entry_style.card_accent_color}
                          onChange={(e) => patchEntryStyleCard({ card_accent_color: e.target.value })}
                        />
                      </label>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                      <p className="mb-2 text-xs font-medium text-slate-600">实时预览（单条示意）</p>
                      <div style={entryStylePreviewCardStyle(ruleDraft.entry_style)}>
                        {ruleDraft.show_info_no ? (
                          <p style={{ ...entryStyleTextCss(ruleDraft.entry_style.info_no), margin: '0 0 6px' }}>
                            编号 2508190001
                          </p>
                        ) : null}
                        <p style={{ ...entryStyleTextCss(ruleDraft.entry_style.title), margin: '0 0 6px' }}>
                          喜德盛 AD350 · 几乎全新
                        </p>
                        <p style={{ ...entryStyleTextCss(ruleDraft.entry_style.site), margin: '0 0 10px' }}>
                          宝华站
                        </p>
                        <div
                          style={{
                            ...entryStyleTextCss(ruleDraft.entry_style.body),
                            whiteSpace: 'pre-wrap',
                            margin: 0,
                          }}
                        >
                          车型：喜德盛 AD350{'\n'}码表：迈金 C506 · 现价三千
                        </div>
                        {ruleDraft.show_phone ? (
                          <p style={{ ...entryStyleTextCss(ruleDraft.entry_style.phone), margin: '12px 0 0' }}>
                            <span style={entryStyleTextCss(ruleDraft.entry_style.phone_label)}>电话 </span>
                            18251863865
                          </p>
                        ) : null}
                        {needsMpLink ? (
                          <p style={{ ...entryStyleTextCss(ruleDraft.entry_style.mp_link), marginTop: 12 }}>
                            {ruleDraft.mp_link_text || '查看详情'} →
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <details className="rounded-lg border border-slate-200 bg-white">
                      <summary className="cursor-pointer px-3 py-2.5 text-sm font-medium text-slate-800">
                        各文字块排版（颜色 / 字号 / 粗细 / 斜体 / 下划线）
                      </summary>
                      <div className="space-y-4 border-t border-slate-100 px-3 py-3">
                        {ENTRY_STYLE_TEXT_KEYS.map(({ key, label }) => {
                          if (key === 'mp_link' && !needsMpLink) return null;
                          if ((key === 'phone' || key === 'phone_label') && !ruleDraft.show_phone) return null;
                          if (key === 'info_no' && !ruleDraft.show_info_no) return null;
                          const t = ruleDraft.entry_style[key];
                          return (
                            <div key={key} className="rounded-lg border border-slate-100 bg-slate-50/50 p-3">
                              <p className="mb-2 text-xs font-semibold text-slate-700">{label}</p>
                              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                                <label className={labelClass}>
                                  <span>颜色</span>
                                  <input
                                    type="color"
                                    className="h-9 w-full cursor-pointer rounded border border-slate-200 bg-white p-1"
                                    value={t.color}
                                    onChange={(e) => patchEntryStyleText(key, { color: e.target.value })}
                                  />
                                </label>
                                <label className={labelClass}>
                                  <span>字号 (px)</span>
                                  <input
                                    type="number"
                                    min={10}
                                    max={28}
                                    className={inputClass}
                                    value={t.size_px}
                                    onChange={(e) =>
                                      patchEntryStyleText(key, {
                                        size_px: Math.min(28, Math.max(10, parseInt(e.target.value, 10) || 15)),
                                      })
                                    }
                                  />
                                </label>
                                <label className={`${labelClass} flex items-end gap-2 pb-2`}>
                                  <input
                                    type="checkbox"
                                    checked={t.bold}
                                    onChange={(e) => patchEntryStyleText(key, { bold: e.target.checked })}
                                  />
                                  <span>加粗</span>
                                </label>
                                <label className={`${labelClass} flex items-end gap-2 pb-2`}>
                                  <input
                                    type="checkbox"
                                    checked={t.italic}
                                    onChange={(e) => patchEntryStyleText(key, { italic: e.target.checked })}
                                  />
                                  <span>斜体</span>
                                </label>
                                <label className={`${labelClass} flex items-end gap-2 pb-2`}>
                                  <input
                                    type="checkbox"
                                    checked={t.underline}
                                    onChange={(e) => patchEntryStyleText(key, { underline: e.target.checked })}
                                  />
                                  <span>下划线</span>
                                </label>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </details>
                  </>
                ) : null}
              </div>
            </div>
          </Modal>

          <Modal
            open={previewModalOpen}
            title="预览汇总（可复制）"
            wide="xl"
            onClose={() => {
              setPreviewModalOpen(false);
              setCopyTip('');
            }}
            footer={
              <div className="flex flex-wrap items-center gap-3">
                <Button type="button" variant="primary" onClick={() => void copyPreviewToClipboard()}>
                  复制正文
                </Button>
                {copyTip ? <span className="text-sm text-slate-600">{copyTip}</span> : null}
                <Button type="button" variant="secondary" onClick={() => setPreviewModalOpen(false)}>
                  关闭
                </Button>
              </div>
            }
          >
            <p className="mb-2 whitespace-pre-wrap text-xs text-slate-600">{previewHead}</p>
            <p className="mb-2 text-xs text-slate-500">
              当前格式：
              {previewBodyFormat === 'layout'
                ? ' 常用版式（HTML）'
                : previewBodyFormat === 'rich_html'
                  ? ' HTML 分块'
                  : ' 纯文本 + 分隔行'}
            </p>
            {previewHtml || /<\s*(?:p|img|div|section|br|h[1-6]|a)\b/i.test(previewClipboard) ? (
              <div className="mb-3 max-h-[min(42vh,360px)] overflow-auto rounded-lg border border-slate-200 bg-white p-4 text-[15px] leading-relaxed text-slate-900 [&_img]:max-h-[180px] [&_img]:w-auto [&_img]:max-w-full">
                <p className="mb-2 text-xs font-medium text-slate-500">渲染预览</p>
                <div dangerouslySetInnerHTML={{ __html: previewHtml || previewClipboard }} />
              </div>
            ) : null}
            <pre className="max-h-[min(36vh,280px)] overflow-auto whitespace-pre-wrap break-words rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-900">
              {previewClipboard}
            </pre>
          </Modal>
        </>
      )}
    </div>
  );
}
